/**
 * Catalog / PIM handlers matching the ratified CODEX-3 contract
 * (internal/catalog/openapi-draft/catalog.openapi.yaml). Paths use the
 * `/api/v1` base; problem bodies stay inside the contract's closed extension
 * vocabulary (no invented codes or members).
 */
import { HttpResponse, http } from "msw";
import {
  attemptedCycleComponent,
  attributes as attributeSeeds,
  categories,
  makeProductImportRow,
  productAttributes,
  productComponents as productComponentSeeds,
  productImportFilename,
  productImportRows,
  productImports,
  products as productSeeds,
  taxClasses,
  uomConversions,
  uoms,
  variantBarcodes as variantBarcodeSeeds,
  variants as variantSeeds,
  PROD_NEW,
  type Attribute,
  type Product,
  type ProductAttributeAssignment,
  type ProductComponent,
  type ProductImport,
  type ProductImportNormalizedRow,
  type ProductImportRow,
  type Uom,
  type UomConversion,
  type Variant,
  type VariantBarcode,
} from "../fixtures/index";
import { fixtureId } from "../fixtures/ids";
import {
  catalogAttributeInUse,
  catalogAttributeOptionInUse,
  catalogBarcodeDuplicate,
  catalogBomCycleDetected,
  catalogImportAlreadyCommitted,
  catalogImportUnknownHeader,
  catalogUomUnconvertible,
  conflictProblem,
  notFound,
  validationProblem,
  type Problem,
} from "../helpers/problem";
import {
  api,
  bumpEtag,
  idempotentPost,
  ifMatchGuard,
  jsonWithEtag,
  listResponse,
  onResetMockState,
  problemResponse,
} from "./util";

/** CatalogProduct projection: fixture row + the derived variant_count. */
const productResource = (productId: string) => {
  const product = productsState.find((p) => p.id === productId)!;
  return {
    ...product,
    variant_count: variantsState.filter((v) => v.product_id === productId).length,
  };
};

/** 409 with a field-level `errors[]` entry (RHF-mappable conflict). */
const conflictWithField = (code: string, field: string, message: string): Problem => ({
  ...conflictProblem(code, message),
  errors: [{ field, code, message }],
});

/** Closed 422 used when a new reference targets a terminal UoM. */
const archivedUomReference = (field: string, code: string): Problem =>
  validationProblem(
    [
      {
        field,
        code: "uom_archived",
        message: `${code} is archived and cannot be selected for a new reference.`,
      },
    ],
    "Choose an active unit of measure.",
  );

/** The ratified failures.csv column set (also the accepted upload headers). */
const IMPORT_CSV_COLUMNS = [
  "action",
  "sku",
  "existing_sku",
  "product_name",
  "product_type",
  "base_uom_code",
  "category_code",
  "tax_class_code",
  "product_status",
  "variant_status",
  "attribute_values",
  "row_no",
  "errors",
];

const SPREADSHEET_CELL_GUARD = "\u200B";
const FORMULA_PREFIX = /^[=+\-@\t\r\n\uFF1D\uFF0B\uFF0D\uFF20]/u;

/** Reversible spreadsheet-formula guard; doubling preserves literal guards. */
function protectSpreadsheetCell(value: string): string {
  if (value.startsWith(SPREADSHEET_CELL_GUARD)) return SPREADSHEET_CELL_GUARD + value;
  return FORMULA_PREFIX.test(value) ? SPREADSHEET_CELL_GUARD + value : value;
}

/** Restore only the server's reversible guard; uploaded data stays byte-exact. */
function restoreSpreadsheetCell(value: string): string {
  if (value.startsWith(SPREADSHEET_CELL_GUARD + SPREADSHEET_CELL_GUARD)) {
    return value.slice(SPREADSHEET_CELL_GUARD.length);
  }
  const unguarded = value.slice(SPREADSHEET_CELL_GUARD.length);
  return value.startsWith(SPREADSHEET_CELL_GUARD) && FORMULA_PREFIX.test(unguarded)
    ? unguarded
    : value;
}

const csvCell = (value: string): string => `"${protectSpreadsheetCell(value).replace(/"/g, '""')}"`;

/** Small RFC 4180 reader for the mock import flow (quoted commas/newlines/quotes included). */
function parseCsvRecords(input: string): string[][] | null {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  let afterQuote = false;

  for (let index = 0; index < input.length; index += 1) {
    const char = input[index];
    if (quoted) {
      if (char !== '"') {
        field += char;
        continue;
      }
      if (input[index + 1] === '"') {
        field += '"';
        index += 1;
      } else {
        quoted = false;
        afterQuote = true;
      }
      continue;
    }

    if (afterQuote) {
      if (char === ",") {
        row.push(field);
        field = "";
        afterQuote = false;
      } else if (char === "\n" || char === "\r") {
        if (char === "\r" && input[index + 1] === "\n") index += 1;
        row.push(field);
        rows.push(row);
        row = [];
        field = "";
        afterQuote = false;
      } else {
        // RFC 4180 does not permit unquoted garbage after a closing quote.
        return null;
      }
    } else if (char === '"') {
      if (field !== "") return null;
      quoted = true;
    } else if (char === ",") {
      row.push(field);
      field = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && input[index + 1] === "\n") index += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += char;
    }
  }

  if (quoted) return null;
  if (field !== "" || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  if (rows[0]?.[0]?.startsWith("\uFEFF")) rows[0][0] = rows[0][0].slice(1);
  return rows.filter((record) => record.some((cell) => cell !== ""));
}

function normalizedImportRow(header: readonly string[], cells: readonly string[]) {
  const value = (column: string): string | undefined => {
    const index = header.indexOf(column);
    const cell = index === -1 ? "" : (cells[index] ?? "");
    return cell === "" ? undefined : cell;
  };
  const action = value("action") === "rename" ? "rename" : "upsert";
  const existingSku = value("existing_sku");
  const productName = value("product_name");
  const productType = value("product_type");
  const baseUomCode = value("base_uom_code");
  const categoryCode = value("category_code");
  const taxClassCode = value("tax_class_code");
  const productStatus = value("product_status");
  const variantStatus = value("variant_status");
  const rawAttributes = value("attribute_values");
  let attributeValues: ProductImportNormalizedRow["attribute_values"];
  if (rawAttributes !== undefined) {
    try {
      const parsed = JSON.parse(rawAttributes) as unknown;
      if (
        Array.isArray(parsed) &&
        parsed.every(
          (item) =>
            typeof item === "object" &&
            item !== null &&
            typeof (item as Record<string, unknown>)["code"] === "string" &&
            typeof (item as Record<string, unknown>)["value"] === "string",
        )
      ) {
        attributeValues = parsed as ProductImportNormalizedRow["attribute_values"];
      }
    } catch {
      // Validation errors belong on the staged row; never invent a partial JSON projection.
    }
  }

  return {
    action,
    sku: value("sku") ?? "",
    ...(existingSku === undefined ? {} : { existing_sku: existingSku }),
    ...(productName === undefined ? {} : { product_name: productName }),
    ...(productType === "goods" || productType === "service" || productType === "bundle"
      ? { product_type: productType }
      : {}),
    ...(baseUomCode === undefined ? {} : { base_uom_code: baseUomCode }),
    ...(categoryCode === undefined ? {} : { category_code: categoryCode }),
    ...(taxClassCode === undefined ? {} : { tax_class_code: taxClassCode }),
    ...(productStatus === "draft" || productStatus === "active" || productStatus === "archived"
      ? { product_status: productStatus }
      : {}),
    ...(variantStatus === "active" || variantStatus === "inactive" || variantStatus === "archived"
      ? { variant_status: variantStatus }
      : {}),
    ...(attributeValues === undefined ? {} : { attribute_values: attributeValues }),
  } satisfies ProductImportNormalizedRow;
}

// ── Mutable state (seeded from fixtures) ─────────────────────────────────

interface ImportState {
  record: ProductImport;
  rows: ProductImportRow[];
}

type Mutable<T> = { -readonly [K in keyof T]: T[K] };

const importsState = new Map<string, ImportState>();
let importCounter = 0;
let productCounter = 0;
let variantCounter = 0;
let barcodeCounter = 0;
let componentCounter = 0;
let attributeCounter = 0;
let uomCounter = 0;

/** Per-product attribute assignments — mutable copy of the fixture rows. */
let productsState: Product[] = [];
let variantsState: Variant[] = [];
let attributesState: Attribute[] = [];
let componentsState: ProductComponent[] = [];
let barcodesState: VariantBarcode[] = [];
let assignmentsState: Mutable<ProductAttributeAssignment>[] = [];
let uomsState: Uom[] = [];
let uomConversionsState: UomConversion[] = [];

/** ETag-registry key for one assignment (composite {product, attribute}). */
const assignmentEtagKey = (productId: string, attributeId: string): string =>
  `${productId}/attributes/${attributeId}`;

function seedCatalogState(): void {
  importsState.clear();
  importCounter = 0;
  productCounter = 0;
  variantCounter = 0;
  barcodeCounter = 0;
  componentCounter = 0;
  attributeCounter = 0;
  uomCounter = 0;
  productsState = productSeeds.map((product) => ({ ...product }));
  variantsState = variantSeeds.map((variant) => ({
    ...variant,
    attribute_values: { ...variant.attribute_values },
  }));
  attributesState = attributeSeeds.map((attribute) => ({
    ...attribute,
    options: [...attribute.options],
  }));
  componentsState = productComponentSeeds.map((component) => ({ ...component }));
  barcodesState = variantBarcodeSeeds.map((barcode) => ({ ...barcode }));
  assignmentsState = productAttributes.map((assignment) => ({ ...assignment }));
  uomsState = uoms.map((uom) => ({ ...uom }));
  uomConversionsState = uomConversions.map((conversion) => ({ ...conversion }));
  for (const record of productImports) {
    importsState.set(record.id, {
      record: { ...record },
      rows: productImportRows
        .filter((row) => row.import_id === record.id)
        .map((row) => ({ ...row })),
    });
  }
}
seedCatalogState();
onResetMockState(seedCatalogState);

export const catalogHandlers = [
  // GET /products?sku=&status=&category_id=&cursor=&limit=
  http.get(api("/products"), ({ request }) => {
    const url = new URL(request.url);
    const sku = url.searchParams.get("sku");
    const status = url.searchParams.get("status");
    const categoryId = url.searchParams.get("category_id");
    let result = productsState;
    const matched = new Map<string, { variant_id: string; sku: string }>();
    if (sku !== null) {
      // Exact citext equality per the ratified contract — never a substring.
      const needle = sku.toLowerCase();
      for (const variant of variantsState) {
        if (variant.sku.toLowerCase() === needle) {
          matched.set(variant.product_id, { variant_id: variant.id, sku: variant.sku });
        }
      }
      result = result.filter((p) => matched.has(p.id));
    }
    if (status) result = result.filter((p) => p.status === status);
    if (categoryId) result = result.filter((p) => p.category_id === categoryId);
    return listResponse(
      request,
      result.map((p) => {
        const hint = matched.get(p.id);
        return {
          ...productResource(p.id),
          ...(hint !== undefined ? { matched_variant: hint } : {}),
        };
      }),
      (p) => p.id,
    );
  }),

  // GET /products/:id — CatalogProduct + readOnly variants; strong 404; ETag.
  http.get(api("/products/:id"), ({ params }) => {
    const productId = String(params["id"]);
    if (!productsState.some((p) => p.id === productId)) {
      return problemResponse(notFound("product"));
    }
    return jsonWithEtag(productId, {
      ...productResource(productId),
      variants: variantsState.filter((v) => v.product_id === productId),
    });
  }),

  // PUT /products/:id — If-Match guarded; 412 on stale ETag.
  http.put(api("/products/:id"), async ({ request, params }) => {
    const productId = String(params["id"]);
    const productIndex = productsState.findIndex((product) => product.id === productId);
    if (productIndex === -1) return problemResponse(notFound("product"));
    const product = productsState[productIndex]!;
    // Body parsing may yield. Complete it before entering the optimistic-
    // concurrency critical section so two same-version requests cannot both
    // validate and later commit.
    const body = (await request.json()) as Record<string, unknown>;
    const guard = ifMatchGuard(request, productId, {
      catalogProblem: true,
      deferVersionBump: true,
    });
    if (guard) return guard;
    const requestedBaseUomId = body["base_uom_id"];
    const requestedBaseUom =
      typeof requestedBaseUomId === "string"
        ? uomsState.find((uom) => uom.id === requestedBaseUomId)
        : undefined;
    if (
      requestedBaseUomId !== product.base_uom_id &&
      requestedBaseUom?.status === "archived"
    ) {
      return problemResponse(archivedUomReference("base_uom_id", requestedBaseUom.code));
    }
    const now = new Date().toISOString();
    const inputVariants = Array.isArray(body["variants"])
      ? (body["variants"] as Record<string, unknown>[])
      : undefined;
    if (inputVariants !== undefined) {
      const requestedIds = inputVariants
        .map((variant) => (typeof variant["id"] === "string" ? variant["id"] : ""))
        .filter((id) => id !== "");
      if (new Set(requestedIds).size !== requestedIds.length) {
        return problemResponse(
          validationProblem([
            {
              field: "variants",
              code: "duplicate_variant_id",
              message: "A variant id may occur only once.",
            },
          ]),
        );
      }
      const ownedIds = new Set(
        variantsState
          .filter((variant) => variant.product_id === productId)
          .map((variant) => variant.id),
      );
      const foreignId = requestedIds.find((id) => !ownedIds.has(id));
      if (foreignId !== undefined) {
        return problemResponse(
          validationProblem([
            {
              field: "variants",
              code: "unknown_variant_id",
              message: "An existing variant id must belong to this product.",
            },
          ]),
        );
      }
      const requestedIdSet = new Set(requestedIds);
      const removedIds = [...ownedIds].filter((id) => !requestedIdSet.has(id));
      const referencedId = removedIds.find(
        (id) =>
          barcodesState.some((barcode) => barcode.variant_id === id) ||
          componentsState.some((component) => component.component_variant_id === id),
      );
      if (referencedId !== undefined) {
        return problemResponse(
          conflictProblem(
            "resource_in_use",
            "A referenced variant cannot be removed. Remove or reassign its barcodes and components first.",
          ),
        );
      }
    }
    bumpEtag(productId);
    const echoedVariants: Variant[] =
      inputVariants === undefined
        ? variantsState.filter((variant) => variant.product_id === productId)
        : inputVariants.map((variant) => {
            const requestedId = typeof variant["id"] === "string" ? variant["id"] : "";
            const existing = variantsState.find(
              (candidate) => candidate.product_id === productId && candidate.id === requestedId,
            );
            variantCounter += 1;
            return {
              id: requestedId === "" ? fixtureId("a011", 200 + variantCounter) : requestedId,
              product_id: productId,
              sku: String(variant["sku"] ?? ""),
              ...(typeof variant["name"] === "string" ? { name: variant["name"] } : {}),
              attribute_values: (variant["attribute_values"] ?? {}) as Record<string, string>,
              status: (variant["status"] ?? "active") as Variant["status"],
              created_at: existing?.created_at ?? now,
              updated_at: now,
            };
          });
    const productFields = { ...body };
    delete productFields["variants"];
    productsState[productIndex] = {
      ...product,
      ...productFields,
      id: productId,
      updated_at: now,
    } as Product;
    variantsState = [
      ...variantsState.filter((variant) => variant.product_id !== productId),
      ...echoedVariants,
    ];
    return jsonWithEtag(productId, {
      ...productResource(productId),
      variants: variantsState.filter((variant) => variant.product_id === productId),
    });
  }),

  // POST /products — Idempotency-Key echo/replay + 409 reuse; 201 carries the
  // REQUIRED ETag and Location headers per the ratified create.
  http.post(api("/products"), ({ request }) =>
    idempotentPost(request, (payload) => {
      if (typeof payload["name"] !== "string" || payload["name"].trim() === "") {
        return {
          problem: validationProblem([
            { field: "name", code: "required", message: "Product name is required." },
          ]),
        };
      }
      const baseUom = uomsState.find((uom) => uom.id === payload["base_uom_id"]);
      if (baseUom?.status === "archived") {
        return { problem: archivedUomReference("base_uom_id", baseUom.code) };
      }
      productCounter += 1;
      const productId = productCounter === 1 ? PROD_NEW : fixtureId("a010", 100 + productCounter);
      const now = new Date().toISOString();
      const inputVariants = Array.isArray(payload["variants"])
        ? (payload["variants"] as Record<string, unknown>[])
        : [];
      const body = { ...payload };
      delete body["variants"];
      const product = {
        description: "",
        status: "draft",
        ...body,
        id: productId,
        created_at: now,
        updated_at: now,
      } as Product;
      productsState.push(product);
      const createdVariants: Variant[] = inputVariants.map((variant) => {
        variantCounter += 1;
        return {
          id: fixtureId("a011", 300 + variantCounter),
          product_id: productId,
          sku: String(variant["sku"] ?? ""),
          ...(typeof variant["name"] === "string" ? { name: variant["name"] } : {}),
          attribute_values: (variant["attribute_values"] ?? {}) as Record<string, string>,
          status: (variant["status"] ?? "active") as Variant["status"],
          created_at: now,
          updated_at: now,
        };
      });
      variantsState.push(...createdVariants);
      return {
        status: 201,
        body: productResource(productId),
        etagFor: productId,
        location: `/api/v1/products/${productId}`,
      };
    }),
  ),

  // GET /products/:id/attributes — the per-product attribute assignments that
  // drive the editor's variant-grid columns (ordered by position).
  http.get(api("/products/:id/attributes"), ({ request, params }) => {
    const productId = String(params["id"]);
    if (!productsState.some((p) => p.id === productId)) {
      return problemResponse(notFound("product"));
    }
    const assigned = [...assignmentsState]
      .filter((assignment) => assignment.product_id === productId)
      .sort((a, b) => a.position - b.position || a.attribute_id.localeCompare(b.attribute_id));
    return listResponse(request, assigned, (assignment) => assignment.attribute_id);
  }),

  // POST /products/:id/attributes — assign a definition; 201 carries the
  // assignment's own initial ETag (the validator later PATCHes replay).
  http.post(api("/products/:id/attributes"), ({ request, params }) => {
    const productId = String(params["id"]);
    if (!productsState.some((p) => p.id === productId)) {
      return problemResponse(notFound("product"));
    }
    return idempotentPost(request, (payload) => {
      const attributeId = String(payload["attribute_id"] ?? "");
      if (!attributesState.some((a) => a.id === attributeId)) {
        return {
          problem: validationProblem([
            { field: "attribute_id", code: "unknown_attribute", message: "Unknown attribute." },
          ]),
        };
      }
      if (
        assignmentsState.some((a) => a.product_id === productId && a.attribute_id === attributeId)
      ) {
        return {
          problem: conflictWithField(
            "duplicate_assignment",
            "attribute_id",
            "The attribute is already assigned to this product.",
          ),
        };
      }
      const now = new Date().toISOString();
      const row: ProductAttributeAssignment = {
        product_id: productId,
        attribute_id: attributeId,
        is_required: payload["is_required"] === true,
        is_variant_axis: payload["is_variant_axis"] === true,
        position: typeof payload["position"] === "number" ? payload["position"] : 0,
        created_at: now,
        updated_at: now,
      };
      assignmentsState.push(row);
      return {
        status: 201,
        body: row as unknown as Record<string, unknown>,
        etagFor: assignmentEtagKey(productId, attributeId),
      };
    });
  }),

  // GET /products/:id/attributes/:attributeId — detail carries the ETag.
  http.get(api("/products/:id/attributes/:attributeId"), ({ params }) => {
    const productId = String(params["id"]);
    const attributeId = String(params["attributeId"]);
    if (!productsState.some((p) => p.id === productId)) {
      return problemResponse(notFound("product"));
    }
    const row = assignmentsState.find(
      (a) => a.product_id === productId && a.attribute_id === attributeId,
    );
    if (row === undefined) return problemResponse(notFound("product attribute"));
    return jsonWithEtag(assignmentEtagKey(productId, attributeId), row);
  }),

  // PATCH /products/:id/attributes/:attributeId — merge-patch guarded by the
  // ASSIGNMENT's own If-Match; 412 on stale ETag.
  http.patch(api("/products/:id/attributes/:attributeId"), async ({ request, params }) => {
    const productId = String(params["id"]);
    const attributeId = String(params["attributeId"]);
    if (!productsState.some((p) => p.id === productId)) {
      return problemResponse(notFound("product"));
    }
    const row = assignmentsState.find(
      (a) => a.product_id === productId && a.attribute_id === attributeId,
    );
    if (row === undefined) return problemResponse(notFound("product attribute"));
    const guard = ifMatchGuard(request, assignmentEtagKey(productId, attributeId), {
      catalogProblem: true,
    });
    if (guard) return guard;
    const body = (await request.json()) as Record<string, unknown>;
    if (typeof body["is_required"] === "boolean") row.is_required = body["is_required"];
    if (typeof body["is_variant_axis"] === "boolean") row.is_variant_axis = body["is_variant_axis"];
    if (typeof body["position"] === "number") row.position = body["position"];
    row.updated_at = new Date().toISOString();
    return jsonWithEtag(assignmentEtagKey(productId, attributeId), row);
  }),

  // DELETE /products/:id/attributes/:attributeId — 422 attribute_in_use while
  // any of THIS product's variants still carries a value for the attribute.
  http.delete(api("/products/:id/attributes/:attributeId"), ({ params }) => {
    const productId = String(params["id"]);
    const attributeId = String(params["attributeId"]);
    if (!productsState.some((p) => p.id === productId)) {
      return problemResponse(notFound("product"));
    }
    const index = assignmentsState.findIndex(
      (a) => a.product_id === productId && a.attribute_id === attributeId,
    );
    if (index === -1) return problemResponse(notFound("product attribute"));
    const code = attributesState.find((a) => a.id === attributeId)?.code;
    const usageCount =
      code === undefined
        ? 0
        : variantsState.filter(
            (variant) => variant.product_id === productId && code in variant.attribute_values,
          ).length;
    if (usageCount > 0) {
      return problemResponse(
        catalogAttributeInUse(
          attributeId,
          usageCount,
          "The attribute cannot be unassigned while variants carry values for it.",
        ),
      );
    }
    assignmentsState.splice(index, 1);
    return new HttpResponse(null, { status: 204 });
  }),

  // GET /variants?barcode=&q= — the global scan-box resolver (0-or-1 by barcode).
  http.get(api("/variants"), ({ request }) => {
    const url = new URL(request.url);
    const barcode = url.searchParams.get("barcode");
    const q = url.searchParams.get("q")?.toLowerCase() ?? null;
    let result = variantsState;
    if (barcode !== null) {
      const hit = barcodesState.find((b) => b.barcode === barcode);
      result = hit ? variantsState.filter((v) => v.id === hit.variant_id) : [];
    } else if (q !== null) {
      result = variantsState.filter((v) => v.sku.toLowerCase().includes(q));
    }
    return listResponse(request, result, (v) => v.id);
  }),

  // GET /products/:id/components — bundle/BOM lines (position order).
  http.get(api("/products/:id/components"), ({ request, params }) => {
    const productId = String(params["id"]);
    if (!productsState.some((p) => p.id === productId)) {
      return problemResponse(notFound("product"));
    }
    const url = new URL(request.url);
    const componentType = url.searchParams.get("component_type");
    let result = componentsState.filter((c) => c.parent_product_id === productId);
    if (componentType) result = result.filter((c) => c.component_type === componentType);
    result = [...result].sort((a, b) => a.position - b.position || a.id.localeCompare(b.id));
    return listResponse(request, result, (c) => c.id);
  }),

  // POST /products/:id/components — 422 bom_cycle_detected on the fixture's
  // attempted-cycle example (WHEEL-SET ← FRAME-KIT).
  http.post(api("/products/:id/components"), ({ request, params }) => {
    const productId = String(params["id"]);
    if (!productsState.some((p) => p.id === productId)) {
      return problemResponse(notFound("product"));
    }
    return idempotentPost(request, (payload) => {
      const componentVariantId = payload["component_variant_id"];
      if (
        typeof componentVariantId !== "string" ||
        !variantsState.some((variant) => variant.id === componentVariantId)
      ) {
        return {
          problem: validationProblem([
            {
              field: "component_variant_id",
              code: "unknown_variant",
              message: "Unknown component variant.",
            },
          ]),
        };
      }
      const componentUom = uomsState.find((uom) => uom.id === payload["uom_id"]);
      if (componentUom?.status === "archived") {
        return { problem: archivedUomReference("uom_id", componentUom.code) };
      }
      if (
        productId === attemptedCycleComponent.parent_product_id &&
        componentVariantId === attemptedCycleComponent.component_variant_id
      ) {
        return {
          problem: catalogBomCycleDetected(
            attemptedCycleComponent.cycle,
            "Adding FRAME-KIT would create a loop: FRAME-KIT → WHEEL-SET → FRAME-KIT",
          ),
        };
      }
      componentCounter += 1;
      const now = new Date().toISOString();
      const row = {
        position: 0,
        ...payload,
        id: fixtureId("a012", 100 + componentCounter),
        parent_product_id: productId,
        created_at: now,
        updated_at: now,
      } as ProductComponent;
      componentsState.push(row);
      return {
        status: 201,
        body: row as unknown as Record<string, unknown>,
      };
    });
  }),

  // DELETE /products/:id/components/:componentId — remove a BOM/bundle line.
  http.delete(api("/products/:id/components/:componentId"), ({ params }) => {
    const productId = String(params["id"]);
    if (!productsState.some((p) => p.id === productId)) {
      return problemResponse(notFound("product"));
    }
    const componentIndex = componentsState.findIndex(
      (component) =>
        component.parent_product_id === productId && component.id === String(params["componentId"]),
    );
    if (componentIndex === -1) return problemResponse(notFound("product component"));
    componentsState.splice(componentIndex, 1);
    return new HttpResponse(null, { status: 204 });
  }),

  // GET /attributes — the global attribute definitions.
  http.get(api("/attributes"), ({ request }) =>
    listResponse(request, attributesState, (attribute) => attribute.id),
  ),

  // Attributes admin CRUD (detail carries the ETag for If-Match edits).
  http.get(api("/attributes/:id"), ({ params }) => {
    const attribute = attributesState.find((a) => a.id === String(params["id"]));
    if (!attribute) return problemResponse(notFound("attribute"));
    return jsonWithEtag(attribute.id, attribute);
  }),
  http.post(api("/attributes"), ({ request }) =>
    idempotentPost(request, (payload) => {
      const code = String(payload["code"] ?? "");
      if (attributesState.some((a) => a.code === code)) {
        return { problem: conflictWithField("duplicate_code", "code", "Code already exists.") };
      }
      const type = payload["type"];
      if (code === "" || (type !== "text" && type !== "number" && type !== "select")) {
        return { problem: validationProblem([], "Attribute code and type are required.") };
      }
      attributeCounter += 1;
      const now = new Date().toISOString();
      const row: Attribute = {
        id: fixtureId("a002", 100 + attributeCounter),
        code,
        name: typeof payload["name"] === "string" ? payload["name"] : code,
        type,
        options: Array.isArray(payload["options"])
          ? payload["options"].filter((option): option is string => typeof option === "string")
          : [],
        status: payload["status"] === "archived" ? "archived" : "active",
        created_at: now,
        updated_at: now,
      };
      attributesState.push(row);
      return {
        status: 201,
        body: row as unknown as Record<string, unknown>,
      };
    }),
  ),
  http.put(api("/attributes/:id"), async ({ request, params }) => {
    const attributeId = String(params["id"]);
    const attributeIndex = attributesState.findIndex((a) => a.id === attributeId);
    const attribute = attributesState[attributeIndex];
    if (!attribute) return problemResponse(notFound("attribute"));
    // Request decoding happens outside the synchronous compare/validate/write
    // section. Once If-Match succeeds there is no await before the version bump.
    const body = (await request.json()) as Record<string, unknown>;
    const guard = ifMatchGuard(request, attributeId, {
      catalogProblem: true,
      deferVersionBump: true,
    });
    if (guard) return guard;
    if (body["code"] !== undefined && body["code"] !== attribute.code) {
      return problemResponse(
        validationProblem([
          {
            field: "code",
            code: "immutable",
            message: "Attribute code cannot change after creation.",
          },
        ]),
      );
    }
    const usageCount = variantsState.filter((v) => attribute.code in v.attribute_values).length;
    // Usage guards run before the If-Match version bump so a corrected retry
    // can replay the same captured ETag.
    if (body["type"] !== attribute.type && usageCount > 0) {
      return problemResponse(
        catalogAttributeInUse(
          attributeId,
          usageCount,
          "The type cannot change while variants carry values for this attribute.",
        ),
      );
    }
    if (attribute.type === "select") {
      const nextOptions = Array.isArray(body["options"])
        ? (body["options"] as unknown[]).map(String)
        : [];
      for (const option of attribute.options) {
        if (nextOptions.includes(option)) continue;
        const optionUsage = variantsState.filter(
          (v) => v.attribute_values[attribute.code] === option,
        ).length;
        if (optionUsage > 0) {
          return problemResponse(
            catalogAttributeOptionInUse(
              attributeId,
              option,
              optionUsage,
              "The removed option is still used by variants.",
            ),
          );
        }
      }
    }
    bumpEtag(attributeId);
    const next = {
      ...attribute,
      ...body,
      id: attributeId,
      updated_at: new Date().toISOString(),
    } as Attribute;
    attributesState[attributeIndex] = next;
    return jsonWithEtag(attributeId, next);
  }),
  http.delete(api("/attributes/:id"), ({ params }) => {
    const attributeIndex = attributesState.findIndex((a) => a.id === String(params["id"]));
    const attribute = attributesState[attributeIndex];
    if (!attribute) return problemResponse(notFound("attribute"));
    const assignmentCount = assignmentsState.filter(
      (assignment) => assignment.attribute_id === attribute.id,
    ).length;
    if (assignmentCount > 0) {
      return problemResponse(
        catalogAttributeInUse(
          attribute.id,
          assignmentCount,
          "The definition is assigned to one or more products and cannot be deleted.",
        ),
      );
    }
    const usageCount = variantsState.filter((v) => attribute.code in v.attribute_values).length;
    if (usageCount > 0) {
      return problemResponse(
        catalogAttributeInUse(
          attribute.id,
          usageCount,
          "The definition is still referenced by variants.",
        ),
      );
    }
    attributesState.splice(attributeIndex, 1);
    return new HttpResponse(null, { status: 204 });
  }),

  // UoM admin CRUD. Code is immutable after create; edits carry If-Match.
  http.get(api("/uoms"), ({ request }) => {
    const status = new URL(request.url).searchParams.get("status");
    const result =
      status === null ? uomsState : uomsState.filter((uom) => uom.status === status);
    return listResponse(request, result, (uom) => uom.id);
  }),
  http.post(api("/uoms"), ({ request }) =>
    idempotentPost(request, (payload) => {
      const code = String(payload["code"] ?? "");
      if (uomsState.some((uom) => uom.code.toLowerCase() === code.toLowerCase())) {
        return { problem: conflictWithField("duplicate_code", "code", "Code already exists.") };
      }
      if (
        code === "" ||
        typeof payload["name"] !== "string" ||
        typeof payload["symbol"] !== "string" ||
        typeof payload["dimension"] !== "string"
      ) {
        return { problem: validationProblem([], "Every UoM field is required.") };
      }
      uomCounter += 1;
      const now = new Date().toISOString();
      const row: Uom = {
        id: fixtureId("a001", 100 + uomCounter),
        code,
        name: payload["name"],
        symbol: payload["symbol"],
        dimension: payload["dimension"],
        status: "active",
        created_at: now,
        updated_at: now,
      };
      uomsState.push(row);
      return { status: 201, body: row as unknown as Record<string, unknown> };
    }),
  ),
  http.get(api("/uoms/:id"), ({ params }) => {
    const row = uomsState.find((uom) => uom.id === String(params["id"]));
    return row === undefined ? problemResponse(notFound("uom")) : jsonWithEtag(row.id, row);
  }),
  http.patch(api("/uoms/:id"), async ({ request, params }) => {
    const uomId = String(params["id"]);
    const index = uomsState.findIndex((uom) => uom.id === uomId);
    if (index === -1) return problemResponse(notFound("uom"));
    const guard = ifMatchGuard(request, uomId, {
      catalogProblem: true,
      deferVersionBump: true,
    });
    if (guard) return guard;
    const body = (await request.json()) as Record<string, unknown>;
    const current = uomsState[index]!;
    if (current.status === "archived") {
      return problemResponse(
        conflictProblem("uom_archived", "The archived unit cannot be updated."),
      );
    }
    bumpEtag(uomId);
    const next: Uom = {
      ...current,
      ...(typeof body["name"] === "string" ? { name: body["name"] } : {}),
      ...(typeof body["symbol"] === "string" ? { symbol: body["symbol"] } : {}),
      ...(typeof body["dimension"] === "string" ? { dimension: body["dimension"] } : {}),
      ...(body["status"] === "active" || body["status"] === "archived"
        ? { status: body["status"] }
        : {}),
      updated_at: new Date().toISOString(),
    };
    uomsState[index] = next;
    return jsonWithEtag(uomId, next);
  }),
  http.delete(api("/uoms/:id"), ({ request, params }) => {
    const uomId = String(params["id"]);
    const index = uomsState.findIndex((uom) => uom.id === uomId);
    if (index === -1) return problemResponse(notFound("uom"));
    const guard = ifMatchGuard(request, uomId, {
      catalogProblem: true,
      deferVersionBump: true,
    });
    if (guard) return guard;
    if (uomsState[index]!.status === "archived") {
      return new HttpResponse(null, { status: 204 });
    }
    bumpEtag(uomId);
    uomsState[index] = {
      ...uomsState[index]!,
      status: "archived",
      updated_at: new Date().toISOString(),
    };
    return new HttpResponse(null, { status: 204 });
  }),

  // UoM conversions use the composite {from,to} identity (no synthetic id).
  http.get(api("/uom-conversions"), ({ request }) =>
    listResponse(request, uomConversionsState, (c) => `${c.from_uom_id}/${c.to_uom_id}`),
  ),

  // POST /uom-conversions — 422 uom_unconvertible on dimension mismatch.
  http.post(api("/uom-conversions"), ({ request }) =>
    idempotentPost(request, (payload) => {
      const fromUomId = String(payload["from_uom_id"] ?? "");
      const toUomId = String(payload["to_uom_id"] ?? "");
      const fromUom = uomsState.find((u) => u.id === fromUomId);
      const toUom = uomsState.find((u) => u.id === toUomId);
      if (fromUom === undefined || toUom === undefined) {
        return {
          problem: validationProblem([
            {
              field: fromUom === undefined ? "from_uom_id" : "to_uom_id",
              code: "unknown_uom",
              message: "Unknown unit of measure.",
            },
          ]),
        };
      }
      const archivedUom = fromUom.status === "archived" ? fromUom : toUom.status === "archived" ? toUom : undefined;
      if (archivedUom !== undefined) {
        return {
          problem: archivedUomReference(
            archivedUom.id === fromUomId ? "from_uom_id" : "to_uom_id",
            archivedUom.code,
          ),
        };
      }
      if (fromUom.dimension !== toUom.dimension) {
        return {
          problem: catalogUomUnconvertible(
            fromUomId,
            toUomId,
            `${fromUom.code} (${fromUom.dimension}) cannot convert to ${toUom.code} (${toUom.dimension}).`,
          ),
        };
      }
      if (
        uomConversionsState.some(
          (conversion) => conversion.from_uom_id === fromUomId && conversion.to_uom_id === toUomId,
        )
      ) {
        return {
          problem: conflictWithField(
            "duplicate_conversion",
            "from_uom_id",
            "This conversion already exists.",
          ),
        };
      }
      const now = new Date().toISOString();
      const row: UomConversion = {
        from_uom_id: fromUomId,
        to_uom_id: toUomId,
        factor: String(payload["factor"] ?? ""),
        created_at: now,
        updated_at: now,
      };
      uomConversionsState.push(row);
      return {
        status: 201,
        body: row as unknown as Record<string, unknown>,
      };
    }),
  ),

  // DELETE /uom-conversions/:fromUomId/:toUomId — composite key path.
  http.delete(api("/uom-conversions/:fromUomId/:toUomId"), ({ params }) => {
    const index = uomConversionsState.findIndex(
      (conversion) =>
        conversion.from_uom_id === String(params["fromUomId"]) &&
        conversion.to_uom_id === String(params["toUomId"]),
    );
    if (index === -1) return problemResponse(notFound("uom conversion"));
    uomConversionsState.splice(index, 1);
    return new HttpResponse(null, { status: 204 });
  }),

  // GET /variants/:id/barcodes — the per-variant barcode list.
  http.get(api("/variants/:id/barcodes"), ({ request, params }) => {
    const variantId = String(params["id"]);
    if (!variantsState.some((v) => v.id === variantId)) {
      return problemResponse(notFound("variant"));
    }
    return listResponse(
      request,
      barcodesState.filter((b) => b.variant_id === variantId),
      (b) => b.id,
    );
  }),

  // POST /variants/:id/barcodes — 409 barcode_duplicate names the owning
  // variant in the `owner` extension ({variant_id, sku}) for the UI's link.
  http.post(api("/variants/:id/barcodes"), ({ request, params }) => {
    const variantId = String(params["id"]);
    if (!variantsState.some((v) => v.id === variantId)) {
      return problemResponse(notFound("variant"));
    }
    return idempotentPost(request, (payload) => {
      const barcode = String(payload["barcode"] ?? "");
      const existing = barcodesState.find((b) => b.barcode === barcode);
      if (existing && existing.variant_id !== variantId) {
        const owner = variantsState.find((v) => v.id === existing.variant_id);
        return owner === undefined
          ? {
              problem: conflictProblem(
                "barcode_duplicate",
                `${barcode} is already in use by another variant.`,
              ),
            }
          : {
              problem: catalogBarcodeDuplicate(barcode, {
                variant_id: owner.id,
                sku: owner.sku,
              }),
            };
      }
      // Same barcode on the same variant: replay-safe no-op result.
      if (existing !== undefined) {
        return { status: 201, body: existing as unknown as Record<string, unknown> };
      }
      barcodeCounter += 1;
      const now = new Date().toISOString();
      const row = {
        ...payload,
        id: fixtureId("a013", 100 + barcodeCounter),
        variant_id: variantId,
        created_at: now,
        updated_at: now,
      } as VariantBarcode;
      barcodesState.push(row);
      return {
        status: 201,
        body: row as unknown as Record<string, unknown>,
      };
    });
  }),
  http.delete(api("/variants/:id/barcodes/:barcode"), ({ params }) => {
    const variantId = String(params["id"]);
    if (!variantsState.some((v) => v.id === variantId)) {
      return problemResponse(notFound("variant"));
    }
    const barcodeIndex = barcodesState.findIndex(
      (barcode) =>
        barcode.variant_id === variantId && barcode.barcode === String(params["barcode"]),
    );
    if (barcodeIndex === -1) return problemResponse(notFound("barcode"));
    barcodesState.splice(barcodeIndex, 1);
    return new HttpResponse(null, { status: 204 });
  }),

  // Lookup reference data for the product form selects.
  http.get(api("/tax-classes"), ({ request }) => listResponse(request, taxClasses, (t) => t.id)),
  http.get(api("/categories"), ({ request }) => listResponse(request, categories, (c) => c.id)),

  // ── Product imports (two-phase CSV wizard) ─────────────────────────────
  // GET /product-imports — staged imports, newest state included.
  http.get(api("/product-imports"), ({ request }) =>
    listResponse(
      request,
      [...importsState.values()].map((state) => state.record),
      (record) => record.id,
    ),
  ),

  // POST /product-imports — phase 1: multipart {file, allow_explicit_sku_renames}
  // per the ratified contract; 202 + Location; the client filename is discarded
  // and the record carries the server token. Rows whose SKU contains "DUP"
  // validate as sku_duplicate errors.
  http.post(api("/product-imports"), async ({ request }) => {
    const rawBody = await request.clone().arrayBuffer();
    let content = "";
    let allowRenames = false;
    try {
      const form = await request.formData();
      const file = form.get("file");
      if (file === null) {
        return problemResponse(
          validationProblem([
            { field: "file", code: "required", message: "A CSV file part is required." },
          ]),
        );
      }
      content = typeof file === "string" ? file : await file.text();
      allowRenames = String(form.get("allow_explicit_sku_renames") ?? "false") === "true";
    } catch {
      return problemResponse(validationProblem([], "The upload body is not multipart/form-data."));
    }
    return idempotentPost(
      request,
      (payload) => {
        const csv = String(payload["file"] ?? "");
        const records = parseCsvRecords(csv);
        if (records === null) {
          return {
            problem: validationProblem([
              {
                field: "file",
                code: "invalid_csv",
                message: "The CSV quoting is malformed.",
              },
            ]),
          };
        }
        const header = (records[0] ?? []).map((cell) => cell.trim());
        if (records.length === 0) {
          return {
            problem: validationProblem([
              { field: "file", code: "import_empty_file", message: "The CSV has no rows." },
            ]),
          };
        }
        if (new Set(header).size !== header.length) {
          return {
            problem: validationProblem([
              {
                field: "headers",
                code: "import_duplicate_header",
                message: "CSV header names must be unique.",
              },
            ]),
          };
        }
        if (records.slice(1).some((row) => row.length !== header.length)) {
          return {
            problem: validationProblem([
              {
                field: "file",
                code: "invalid_csv",
                message: "Every CSV row must contain exactly the header column count.",
              },
            ]),
          };
        }
        // Unknown headers reject the whole upload — the header is never echoed.
        if (header.some((cell) => !IMPORT_CSV_COLUMNS.includes(cell))) {
          return {
            problem: catalogImportUnknownHeader(
              "The CSV contains a header outside the product-import contract; nothing was staged.",
            ),
          };
        }
        const skuIdx = header.indexOf("sku");
        if (skuIdx === -1) {
          return {
            problem: validationProblem([
              {
                field: "headers",
                code: "import_missing_header",
                message: "A sku column is required.",
              },
            ]),
          };
        }
        importCounter += 1;
        const id = fixtureId("a100", 100 + importCounter);
        const rows: ProductImportRow[] = records.slice(1).map((rawCells, index) => {
          const cells = rawCells.map(restoreSpreadsheetCell);
          const normalized = normalizedImportRow(header, cells);
          const sku = normalized.sku;
          const bad = sku.includes("DUP");
          return makeProductImportRow(
            importCounter * 100 + index + 1,
            id,
            index + 1,
            sku,
            bad ? "error" : "valid",
            bad ? [{ code: "sku_duplicate", field: "sku", message: "SKU already exists." }] : [],
            normalized,
          );
        });
        const now = new Date();
        const record: ProductImport = {
          id,
          filename: productImportFilename(id),
          // The mock compresses the async uploaded → validating → validated arc.
          status: "validated",
          total_rows: rows.length,
          clean_count: rows.filter((row) => row.status === "valid").length,
          error_count: rows.filter((row) => row.status === "error").length,
          committed_count: 0,
          error_csv_available: rows.some((row) => row.status === "error"),
          staging_expires_at: new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          created_at: now.toISOString(),
          updated_at: now.toISOString(),
        };
        importsState.set(id, { record, rows });
        return {
          status: 202,
          body: record as unknown as Record<string, unknown>,
          location: `/api/v1/product-imports/${id}`,
        };
      },
      {
        rawBody,
        payload: { file: content, allow_explicit_sku_renames: allowRenames },
      },
    );
  }),

  http.get(api("/product-imports/:id"), ({ params }) => {
    const state = importsState.get(String(params["id"]));
    if (!state) return problemResponse(notFound("import"));
    if (state.record.status === "committing") {
      // The async commit batch "finishes" by the time anyone polls again.
      state.record = {
        ...state.record,
        status: "committed",
        committed_count: state.record.clean_count,
        updated_at: new Date().toISOString(),
      };
    }
    return HttpResponse.json(state.record as unknown as Record<string, unknown>);
  }),

  // GET /product-imports/:id/rows?status=&sku= — the staging report.
  http.get(api("/product-imports/:id/rows"), ({ request, params }) => {
    const state = importsState.get(String(params["id"]));
    if (!state) return problemResponse(notFound("import"));
    const url = new URL(request.url);
    const status = url.searchParams.get("status");
    const sku = url.searchParams.get("sku")?.toLowerCase() ?? null;
    let rows = state.rows;
    if (status) rows = rows.filter((row) => row.status === status);
    if (sku !== null) rows = rows.filter((row) => row.sku.toLowerCase().includes(sku));
    return listResponse(request, rows, (row) => row.id);
  }),

  // POST /product-imports/:id/commit — phase 2: async, 202 accepted.
  http.post(api("/product-imports/:id/commit"), ({ request, params }) => {
    const state = importsState.get(String(params["id"]));
    if (!state) return problemResponse(notFound("import"));
    return idempotentPost(request, () => {
      if (state.record.status === "committed" || state.record.status === "committing") {
        return {
          problem: catalogImportAlreadyCommitted(
            state.record.id,
            "This import has already been committed.",
          ),
        };
      }
      state.record = {
        ...state.record,
        status: "committing",
        updated_at: new Date().toISOString(),
      };
      return { status: 202, body: state.record as unknown as Record<string, unknown> };
    });
  }),

  // DELETE /product-imports/:id — discard a staged (uncommitted) import.
  http.delete(api("/product-imports/:id"), ({ params }) => {
    const state = importsState.get(String(params["id"]));
    if (!state) return problemResponse(notFound("import"));
    if (state.record.status === "committed" || state.record.status === "committing") {
      return problemResponse(
        catalogImportAlreadyCommitted(state.record.id, "Committed imports cannot be discarded."),
      );
    }
    state.record = {
      ...state.record,
      status: "discarded",
      updated_at: new Date().toISOString(),
    };
    return new HttpResponse(null, { status: 204 });
  }),

  // GET /product-imports/:id/failures.csv — the ratified re-uploadable CSV:
  // fixed normalized columns + row_no + JSON-encoded errors, served as an
  // attachment named after the server token.
  http.get(api("/product-imports/:id/failures.csv"), ({ params }) => {
    const importId = String(params["id"]);
    const state = importsState.get(importId);
    if (!state) return problemResponse(notFound("import"));
    const lines = [IMPORT_CSV_COLUMNS.join(",")];
    for (const row of state.rows) {
      if (row.status !== "error") continue;
      const normalized = row.normalized;
      lines.push(
        [
          normalized.action,
          normalized.sku,
          normalized.existing_sku ?? "",
          normalized.product_name ?? "",
          normalized.product_type ?? "",
          normalized.base_uom_code ?? "",
          normalized.category_code ?? "",
          normalized.tax_class_code ?? "",
          normalized.product_status ?? "",
          normalized.variant_status ?? "",
          normalized.attribute_values === undefined
            ? ""
            : JSON.stringify(normalized.attribute_values),
          String(row.row_no),
          JSON.stringify(row.errors),
        ]
          .map(csvCell)
          .join(","),
      );
    }
    return new HttpResponse(lines.join("\n"), {
      status: 200,
      headers: {
        "Content-Type": "text/csv",
        "Content-Disposition": `attachment; filename="catalog-import-${importId}-failures.csv"`,
      },
    });
  }),
];
